import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { b2bGetJob, b2bUpdateJob, b2bHaltContact } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

const STATUS_COLORS = {
  draft:     { bg: 'rgba(100,116,139,0.08)', color: '#64748b' },
  active:    { bg: 'rgba(5,150,105,0.08)',   color: '#059669' },
  paused:    { bg: 'rgba(217,119,6,0.08)',   color: '#d97706' },
  halted:    { bg: 'rgba(239,68,68,0.08)',   color: '#dc2626' },
  completed: { bg: 'rgba(1,118,206,0.08)',   color: '#0176CE' },
};

const DAY_OPTIONS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const HOUR_OPTIONS = Array.from({ length: 15 }, (_, i) => {
  const h = i + 6; // 6am – 8pm
  const label = h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;
  return { value: h, label };
});

const RESEARCH_TEXT = `Why does send timing matter?

Research consistently shows that email timing significantly impacts open and reply rates in B2B outreach:

• Best days: Tuesday, Wednesday, and Thursday see the highest open rates — typically 15–20% above Monday or Friday. Weekend sends are rarely effective for business audiences.

• Best times: Mid-morning (9–11am) and early afternoon (1–3pm) in the recipient's local timezone perform best. Emails arriving right when someone starts their day or after lunch get the most attention.

• Timezone awareness: Sending at 10am Eastern to a contact in California means arriving at 7am — before they're in work mode. Matching the send time to the recipient's state dramatically improves engagement.

• Randomizing within a window: Sending all emails at exactly the same time can look like a blast and trigger spam filters. Distributing sends randomly across a 1–2 hour window looks more natural and improves deliverability.

• Recommended starting point: Tue–Thu, 10am–12pm local time. Adjust based on your own data over time.`;

export default function B2BMarketingJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Schedule settings
  const [schedule, setSchedule] = useState({ send_days: 'mon,tue,wed,thu,fri', send_hour_start: 10, send_hour_end: 12 });
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState('');
  const [showResearch, setShowResearch] = useState(false);

  function load() {
    setLoading(true);
    b2bGetJob(id).then(r => {
      setData(r);
      setSchedule({
        send_days:       r.job.send_days        || 'mon,tue,wed,thu,fri',
        send_hour_start: r.job.send_hour_start  ?? 10,
        send_hour_end:   r.job.send_hour_end    ?? 12,
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, [id]);

  async function setJobStatus(status) {
    setUpdating(true);
    try { await b2bUpdateJob(id, { status }); load(); }
    catch (err) { alert(err.message); }
    setUpdating(false);
  }

  async function haltContact(contactId) {
    try { await b2bHaltContact(id, contactId); load(); }
    catch (err) { alert(err.message); }
  }

  async function saveName() {
    if (!draftName.trim()) return;
    setSavingName(true);
    try {
      await b2bUpdateJob(id, { job_name: draftName.trim() });
      setEditingName(false);
      load();
    } catch (e) { alert(e.message); }
    setSavingName(false);
  }

  async function saveSchedule() {
    setScheduleSaving(true);
    setScheduleMsg('');
    try {
      await b2bUpdateJob(id, schedule);
      setScheduleMsg('Saved!');
      setTimeout(() => setScheduleMsg(''), 2500);
    } catch (e) {
      setScheduleMsg('Error: ' + e.message);
    }
    setScheduleSaving(false);
  }

  function toggleDay(key) {
    const current = schedule.send_days ? schedule.send_days.split(',').map(d => d.trim()) : [];
    const next = current.includes(key) ? current.filter(d => d !== key) : [...current, key];
    // Keep in DAY_OPTIONS order
    const ordered = DAY_OPTIONS.map(d => d.key).filter(k => next.includes(k));
    setSchedule(s => ({ ...s, send_days: ordered.join(',') }));
  }

  if (loading) return <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}><AdminSidebar active="b2b-jobs" /><main style={{ padding: 40, color: '#64748b' }}>Loading…</main></div>;
  if (!data) return <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}><AdminSidebar active="b2b-jobs" /><main style={{ padding: 40, color: '#ef4444' }}>Not found</main></div>;

  const { job, enrollments, stats } = data;
  const sc = STATUS_COLORS[job.status] || STATUS_COLORS.draft;

  const filtered = enrollments.filter(e => {
    if (filterStatus !== 'all' && e.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return [e.first_name, e.last_name, e.email, e.company_name].some(v => v?.toLowerCase().includes(q));
    }
    return true;
  });

  const activeDays = schedule.send_days ? schedule.send_days.split(',').map(d => d.trim()) : [];

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-jobs" />
      <main style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/b2b/jobs')}>← Marketing Jobs</button>

        <div style={s.header}>
          <div style={{ flex: 1 }}>
            {editingName ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <input
                  autoFocus
                  value={draftName}
                  onChange={e => setDraftName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  style={{ fontSize: 20, fontWeight: 700, color: NAVY, border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', width: 400 }}
                />
                <button style={s.launchBtn} onClick={saveName} disabled={savingName}>{savingName ? 'Saving…' : 'Save'}</button>
                <button style={s.pauseBtn} onClick={() => setEditingName(false)}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h1 style={s.title}>{job.job_name}</h1>
                <button onClick={() => { setDraftName(job.job_name); setEditingName(true); }}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 14, padding: '0 4px', lineHeight: 1 }}
                  title="Rename job">✎</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ ...sc, background: sc.bg, borderRadius: 4, fontSize: 11, fontWeight: 700, padding: '3px 8px', textTransform: 'uppercase' }}>{job.status}</span>
              <span style={{ color: '#64748b', fontSize: 13 }}>List: <strong>{job.list_name}</strong></span>
              <span style={{ color: '#64748b', fontSize: 13 }}>Stream: <strong>{job.stream_name}</strong></span>
              <span style={{ color: '#64748b', fontSize: 13 }}>From: <strong>{job.from_name} &lt;{job.from_email}&gt;</strong></span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {job.status === 'draft' && <button style={s.launchBtn} disabled={updating} onClick={() => setJobStatus('active')}>Launch Job</button>}
            {job.status === 'active' && <button style={s.pauseBtn} disabled={updating} onClick={() => setJobStatus('paused')}>Pause</button>}
            {job.status === 'paused' && <button style={s.launchBtn} disabled={updating} onClick={() => setJobStatus('active')}>Resume</button>}
          </div>
        </div>

        {/* Stats */}
        <div style={s.statsRow}>
          {[
            ['Total Enrolled', stats?.total || 0, NAVY],
            ['Active', stats?.active || 0, '#059669'],
            ['Halted', stats?.halted || 0, '#dc2626'],
            ['Paused', stats?.paused || 0, '#d97706'],
            ['Completed', stats?.completed || 0, BLUE],
          ].map(([label, val, color]) => (
            <div key={label} style={s.statCard}>
              <div style={s.statLabel}>{label}</div>
              <div style={{ ...s.statVal, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Schedule Settings */}
        <div style={s.scheduleCard}>
          <div style={s.scheduleHeader}>
            <div>
              <span style={s.scheduleTitle}>Send Schedule</span>
              <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 8 }}>Controls when emails go out for this job</span>
            </div>
            <button style={s.whyBtn} onClick={() => setShowResearch(true)}>Why does this matter?</button>
          </div>

          <div style={s.scheduleBody}>
            {/* Day picker */}
            <div style={s.scheduleGroup}>
              <label style={s.scheduleLabel}>Send Days</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAY_OPTIONS.map(d => (
                  <button
                    key={d.key}
                    style={{ ...s.dayPill, ...(activeDays.includes(d.key) ? s.dayPillActive : {}) }}
                    onClick={() => toggleDay(d.key)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time range */}
            <div style={s.scheduleGroup}>
              <label style={s.scheduleLabel}>Send Window (recipient's local time)</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <select
                  style={s.timeSelect}
                  value={schedule.send_hour_start}
                  onChange={e => setSchedule(s => ({ ...s, send_hour_start: parseInt(e.target.value) }))}
                >
                  {HOUR_OPTIONS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                </select>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>to</span>
                <select
                  style={s.timeSelect}
                  value={schedule.send_hour_end}
                  onChange={e => setSchedule(s => ({ ...s, send_hour_end: parseInt(e.target.value) }))}
                >
                  {HOUR_OPTIONS.filter(h => h.value > schedule.send_hour_start).map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                </select>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>
                  Emails send at a random time within this window, matched to each contact's state timezone.
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button style={s.saveBtn} onClick={saveSchedule} disabled={scheduleSaving}>
                {scheduleSaving ? 'Saving…' : 'Save Schedule'}
              </button>
              {scheduleMsg && <span style={{ color: scheduleMsg.startsWith('Error') ? '#ef4444' : '#059669', fontSize: 13 }}>{scheduleMsg}</span>}
            </div>
          </div>
        </div>

        {/* Enrollments */}
        <div style={s.controls}>
          <input style={s.search} placeholder="Search contacts…" value={search} onChange={e => setSearch(e.target.value)} />
          <div style={s.filterTabs}>
            {['all', 'active', 'halted', 'paused', 'completed'].map(f => (
              <button key={f} style={{ ...s.filterTab, ...(filterStatus === f ? s.filterTabActive : {}) }} onClick={() => setFilterStatus(f)}>{f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}</button>
            ))}
          </div>
        </div>

        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>{['Contact', 'Company', 'Email', 'Step', 'Status', 'Next Send', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} style={s.empty}>No contacts found</td></tr>}
              {filtered.map(e => {
                const ec = STATUS_COLORS[e.status] || STATUS_COLORS.draft;
                return (
                  <tr key={e.enrollment_id} style={s.tr}>
                    <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>
                      <Link to={`/admin/b2b/contacts/${e.contact_id}`} style={{ color: NAVY, textDecoration: 'none' }}>
                        {[e.first_name, e.last_name].filter(Boolean).join(' ') || '—'}
                      </Link>
                    </td>
                    <td style={s.td}>{e.company_name || '—'}</td>
                    <td style={{ ...s.td, color: BLUE, fontSize: 12 }}>{e.email}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{e.current_step}</td>
                    <td style={s.td}><span style={{ background: ec.bg, color: ec.color, borderRadius: 4, fontSize: 10, fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase' }}>{e.status}</span></td>
                    <td style={{ ...s.td, fontSize: 12, color: '#94a3b8' }}>{e.next_send_utc ? new Date(e.next_send_utc).toLocaleString() : '—'}</td>
                    <td style={s.td}>
                      {e.status === 'active' && (
                        <button style={s.haltBtn} onClick={() => haltContact(e.contact_id)}>Halt</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Research popup */}
        {showResearch && (
          <div style={s.overlay} onClick={() => setShowResearch(false)}>
            <div style={s.researchModal} onClick={e => e.stopPropagation()}>
              <div style={s.researchHeader}>
                <strong style={{ color: NAVY, fontSize: 15 }}>Email Timing Research</strong>
                <button style={s.closeBtn} onClick={() => setShowResearch(false)}>✕</button>
              </div>
              <pre style={s.researchBody}>{RESEARCH_TEXT}</pre>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main: { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  backBtn: { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 8px', display: 'block' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title: { color: NAVY, fontSize: 24, fontWeight: 700, margin: '0 0 4px' },
  launchBtn: { background: '#059669', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  pauseBtn: { background: 'rgba(217,119,6,0.1)', border: 'none', borderRadius: 8, color: '#d97706', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 18px' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 },
  statCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px' },
  statLabel: { color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 },
  statVal: { fontSize: 22, fontWeight: 700 },

  // Schedule card
  scheduleCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  scheduleHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  scheduleTitle: { color: NAVY, fontSize: 14, fontWeight: 700 },
  scheduleBody: { display: 'flex', flexDirection: 'column', gap: 16 },
  scheduleGroup: { display: 'flex', flexDirection: 'column', gap: 8 },
  scheduleLabel: { color: '#374151', fontSize: 12, fontWeight: 600 },
  dayPill: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 20, color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '4px 12px' },
  dayPillActive: { background: NAVY, border: `1px solid ${NAVY}`, color: '#fff' },
  timeSelect: { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer' },
  saveBtn: { background: BLUE, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  whyBtn: { background: 'transparent', border: `1px solid ${ORANGE}`, borderRadius: 6, color: ORANGE, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '4px 12px' },

  // Enrollments
  controls: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },
  search: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 240 },
  filterTabs: { display: 'flex', gap: 4 },
  filterTab: { background: 'transparent', border: 'none', borderRadius: 6, color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '5px 12px' },
  filterTabActive: { background: '#fff', color: NAVY, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
  th: { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  empty: { color: '#94a3b8', fontSize: 13, padding: '32px', textAlign: 'center' },
  haltBtn: { background: 'rgba(239,68,68,0.08)', border: 'none', borderRadius: 6, color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: '4px 10px', fontWeight: 500 },

  // Research modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(10,37,64,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  researchModal: { background: '#fff', borderRadius: 14, width: 540, maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  researchHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #e2e8f0' },
  researchBody: { padding: '20px 22px', overflowY: 'auto', fontSize: 13, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: "'Inter', system-ui, sans-serif", margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18, lineHeight: 1 },
};
